import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FolderTree,
  Package,
  ClipboardList,
  Wallet,
  CheckCircle2,
  Circle,
  ArrowRight,
  Clock,
} from 'lucide-react';

import { useAuthStore } from '@/store/auth.store';
import { listCategories, listProducts } from '@/services/catalog';
import { getNewOrdersCount, listDebts } from '@/services/orders';
import { PageHeader, SectionCard } from '@/components/fields';
import { rub } from '@/lib/format';

function daysLeft(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

export default function Dashboard() {
  const supplier = useAuthStore((s) => s.supplier);

  const cats = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  const prods = useQuery({ queryKey: ['products'], queryFn: () => listProducts() });
  const newOrders = useQuery({
    queryKey: ['orders', 'new-count'],
    queryFn: getNewOrdersCount,
    refetchInterval: 15_000,
  });
  const debts = useQuery({ queryKey: ['debts'], queryFn: listDebts });

  const catCount = cats.data?.length ?? 0;
  const prodCount = prods.data?.length ?? 0;
  const activeProds = prods.data?.filter((p) => p.isActive).length ?? 0;
  const newCount = newOrders.data?.count ?? 0;
  const currentDebt = debts.data?.totals.currentDebt ?? 0;

  const hasRequisites = !!supplier?.inn;
  const steps = [
    { done: hasRequisites, label: 'Заполнить реквизиты компании', to: '/profile' },
    { done: catCount > 0, label: 'Добавить категории товаров', to: '/categories' },
    { done: prodCount > 0, label: 'Добавить товары в каталог', to: '/products' },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  const trialLeft = supplier?.subscriptionStatus === 'TRIAL' ? daysLeft(supplier.trialEndsAt) : null;

  return (
    <div>
      <PageHeader
        title={`Здравствуйте${supplier?.name ? `, ${supplier.name}` : ''}`}
        subtitle="Сводка по вашему магазину"
      />

      {supplier?.status === 'PENDING' && (
        <div className="card p-4 mb-4 border-l-4 border-l-amber-400 flex items-start gap-3">
          <Clock size={18} className="text-amber-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-ink">Профиль на модерации</div>
            <p className="text-ink/60 mt-0.5">
              После проверки модератором ваша витрина станет видна заведениям. Пока заполните каталог,
              чтобы стартовать сразу после одобрения.
            </p>
          </div>
        </div>
      )}

      {trialLeft != null && trialLeft <= 3 && (
        <Link
          to="/subscription"
          className="card p-4 mb-4 border-l-4 border-l-orange flex items-center justify-between gap-3 hover:bg-paper transition"
        >
          <div className="text-sm">
            <div className="font-medium text-ink">Пробный период заканчивается через {trialLeft} дн.</div>
            <p className="text-ink/60 mt-0.5">Оплатите подписку 2999 ₽/мес, чтобы сохранить доступ.</p>
          </div>
          <ArrowRight size={18} className="text-orange shrink-0" />
        </Link>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Kpi icon={<FolderTree size={18} />} label="Категории" value={catCount} to="/categories" />
        <Kpi icon={<Package size={18} />} label="Товары" value={`${activeProds}/${prodCount}`} hint="активных" to="/products" />
        <Kpi
          icon={<ClipboardList size={18} />}
          label="Новые заявки"
          value={newCount}
          hint={newCount > 0 ? 'ждут обработки' : 'новых нет'}
          to="/orders"
        />
        <Kpi
          icon={<Wallet size={18} />}
          label="Долги заведений"
          value={rub(currentDebt)}
          hint={currentDebt > 0 ? 'к погашению' : 'нет долгов'}
          to="/debts"
        />
      </div>

      {/* Онбординг */}
      {!allDone && (
        <SectionCard
          title="Запуск магазина"
          description={`Выполнено ${doneCount} из ${steps.length} шагов`}
        >
          <div className="h-1.5 w-full rounded-full bg-line overflow-hidden mb-4">
            <div
              className="h-full bg-orange transition-all"
              style={{ width: `${(doneCount / steps.length) * 100}%` }}
            />
          </div>
          <ul className="space-y-1">
            {steps.map((s) => (
              <li key={s.to}>
                <Link
                  to={s.to}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-paper transition"
                >
                  {s.done ? (
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                  ) : (
                    <Circle size={18} className="text-ink/25 shrink-0" />
                  )}
                  <span className={`flex-1 text-sm ${s.done ? 'text-ink/40 line-through' : 'text-ink'}`}>
                    {s.label}
                  </span>
                  {!s.done && <ArrowRight size={15} className="text-ink/30" />}
                </Link>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {allDone && (
        <SectionCard title="Магазин готов">
          <p className="text-sm text-ink/60">
            Каталог заполнен. {supplier?.status === 'ACTIVE'
              ? 'Заведения видят вашу витрину и могут оформлять заявки.'
              : 'После одобрения модератором витрина станет видна заведениям.'}
          </p>
        </SectionCard>
      )}
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
  hint,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  hint?: string;
  to: string;
}) {
  return (
    <Link to={to} className="card p-4 hover:shadow-md transition">
      <div className="flex items-center gap-2 text-ink/40 mb-2">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="text-2xl font-semibold text-ink leading-none">{value}</div>
      {hint && <div className="text-[11px] text-ink/40 mt-1">{hint}</div>}
    </Link>
  );
}
